import {
    LoopVaultInfoExpanded,
    MeteoraLoopMetadata,
    StrategyDuration,
    TokenListMetadata,
    ParsedOrcaWhirlpool,
    WhirlpoolOffchain,
    ClmmPositionExpanded
} from "@bridgesplit/abf-sdk";
import { WindType } from "@loopscale/pandora-types";

import { BestQuote, BorrowCap, PrincipalMarketStats, QuoteCollateralInfo } from "./market";
import { AbfLoanExpanded, LoanCollateralExpanded } from "./loan";

export interface LoopExpandedBase {
    collateralToken: TokenListMetadata;
    principalToken: TokenListMetadata;
    vaultIdentifier: string;
    loopVault: LoopVaultInfoExpanded;
    // null for coming-soon loops that have no live market backing
    principalStats: PrincipalMarketStats | null;
    borrowCap: BorrowCap | null;
    maxCollateralAmount: number | undefined;
    aumLimit?: number;
    loopWindType: WindType;
    inputToken: TokenListMetadata;
}

export interface LoopExpandedMeteora extends LoopExpandedBase {
    meteoraPool: MeteoraLoopMetadata;
    tokenA: TokenListMetadata;
    tokenB: TokenListMetadata;
}

export interface LoopExpandedOrca extends LoopExpandedBase {
    whirlpool: ParsedOrcaWhirlpool;
    whirlpoolOffchain: WhirlpoolOffchain;
}

export type LoopExpanded = LoopExpandedMeteora | LoopExpandedBase | LoopExpandedOrca;

export interface LoopPositionExpanded {
    loanExpanded: AbfLoanExpanded;
    loopExpanded?: LoopExpanded;
    allowedWindTypes: WindType[];
}

// loop can have a default unwind type, if set, its the first value in the array, ie orca is 1, for principal unwind
export function getDefaultWindType(loopPositionExpanded: LoopPositionExpanded | undefined) {
    if (!loopPositionExpanded) return undefined;
    if (loopPositionExpanded.allowedWindTypes.length === 1) {
        return loopPositionExpanded.allowedWindTypes[0];
    }
    return WindType.Collateral;
}

export interface BestLoopQuoteQuery {
    principalMint: string;
    collateralInfo: QuoteCollateralInfo;
    leverageMultiplier: number;
    duration: StrategyDuration;
    excluded_external_yield?: number[];
}

export interface BestLoopQuote {
    bestQuote: BestQuote;
    netApyPct: number;
}

export interface LoopQuoteExpanded extends BestLoopQuote {
    collateralAmount: number;
    leveragedCollateralAmount: number;
    principalAmount: number;
    leverageMultiplier: number;
}

export type ExternalWindQuoteParams = {
    loopExpanded: LoopExpanded | undefined;
    slippagePercentDecimals: number;
    principalAmount: number | undefined;
    inputAmount: number | undefined;
    inputToken?: TokenListMetadata | undefined;
    skip?: boolean;
    clmmPosition?: ClmmPositionExpanded;
    windMint: string | undefined;
    userWallet: string | undefined;
    principalUsdPrice: number | undefined;

    // Returned by the loop-specific winder.
    // Default: principal token mint. Orca exception: uses an existing NFT mint (from dropdown selection or inferred from a matching empty position).
};

export type ExternalUnwindQuoteParams = {
    slippagePercentDecimals: number | undefined;
    skip?: boolean;
    disablePoll?: boolean;
    windType: WindType | undefined;
    loanCollateral?: LoanCollateralExpanded | undefined;
    loanExpanded?: AbfLoanExpanded | undefined;
    ledgerDebtAmount?: number | undefined;
    debtToRepay?: number | undefined;
    collateralAmount: number | undefined;
    isRollover?: boolean;
    // Rollovers only: ask pandora for per-venue legs so a split rollover can go out as separate txs.
    splitRolloverLegs?: boolean;
    // Output goes back in as collateral rather than repaying debt, so no debt-sized min out.
    isCollateralSwap?: boolean;
    outputToken?: TokenListMetadata | undefined;
};

export interface LoopTokenPriceChange {
    start: number;
    end: number;
    change: number;
}

export type UseBreakevenHook<TParams extends BreakevenBaseParams> = (params: TParams) => number | undefined;

export type PtBreakevenParams = BreakevenBaseParams & {
    ptApy?: number;
};

export type LstBreakevenParams = BreakevenBaseParams & {
    lstApy?: number;
};

export type BreakevenBaseParams = {
    totalCollateralUsd?: number;
    totalDebtUsd?: number;
    feesUsd?: number;
    loanApy?: number;
};
